import { useQuery } from '@tanstack/react-query'
import { Loader2, RotateCcw, Search, User, X } from 'lucide-react'
import { customersApi } from '@/api/customers'
import { DateRangePicker } from '@/components/DateRangePicker'
import { DepartmentScopeFilter } from '@/components/department-scope-filter/DepartmentScopeFilter'
import { EmployeeTreeFilter } from '@/components/employee-tree-filter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useServiceTypeOptions } from '@/hooks/useServiceRecords'
import { cn } from '@/lib/utils'
import type { DepartmentScope } from '@/types/department'
import type { ServiceRecordFilter } from '@/types/service-record'

export interface ServiceRecordFilterValue extends ServiceRecordFilter {
  scope: DepartmentScope
  /** 已选客户名称（仅用于筛选框回显，不参与查询） */
  customer_name?: string
}

interface ServiceRecordFilterBarProps {
  value: ServiceRecordFilterValue
  onChange: (value: ServiceRecordFilterValue) => void
  onSearch: () => void
  onReset: () => void
  searching?: boolean
}

export function ServiceRecordFilterBar({ value, onChange, onSearch, onReset, searching }: ServiceRecordFilterBarProps) {
  const { data: typeOptions } = useServiceTypeOptions()
  const trimmedKeyword = value.customer_name?.trim() ?? ''

  // 客户搜索（选中后置 customer_id）
  const { data: customerResults, isFetching: searchingCustomers } = useQuery({
    queryKey: ['customers', 'select', trimmedKeyword],
    queryFn: () => customersApi.list({ name: trimmedKeyword, page: 1, page_size: 10 }),
    enabled: !value.customer_id && trimmedKeyword.length > 0,
  })

  const customerPicked = !!value.customer_id
  const showCustomerDropdown = !customerPicked && trimmedKeyword.length > 0

  return (
    <div className="flex flex-wrap items-center gap-4">
      <Select
        value={value.service_type_id ? String(value.service_type_id) : 'all'}
        onValueChange={v => onChange({ ...value, service_type_id: v === 'all' ? undefined : Number(v) })}
      >
        <SelectTrigger className="w-[150px]">
          <SelectValue placeholder="全部类型" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">全部类型</SelectItem>
          {(typeOptions ?? []).map(t => (
            <SelectItem key={t.id} value={String(t.id)}>{t.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="relative">
        <User className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="w-[200px] pl-9"
          value={value.customer_name ?? ''}
          onChange={e => onChange({ ...value, customer_name: e.target.value, customer_id: undefined })}
          placeholder="搜索客户"
        />
        {customerPicked ? (
          <button
            type="button"
            aria-label="清除客户筛选"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => onChange({ ...value, customer_id: undefined, customer_name: undefined })}
          >
            <X className="size-4" />
          </button>
        ) : trimmedKeyword.length > 0 && (
          <Loader2 className={cn('absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground', !searchingCustomers && 'opacity-0')} />
        )}
        {showCustomerDropdown && (
          <div className="absolute left-0 right-0 top-full z-10 mt-1.5 overflow-hidden rounded-lg border bg-background shadow-md">
            {searchingCustomers ? (
              <div className="space-y-2 p-2">
                {[0, 1, 2].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : (customerResults?.list ?? []).length > 0 ? (
              <div className="divide-y">
                {(customerResults?.list ?? []).map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors',
                      value.customer_id === c.id ? 'bg-accent' : 'hover:bg-accent/60',
                    )}
                    onClick={() => onChange({ ...value, customer_id: c.id, customer_name: c.name })}
                  >
                    <span className="truncate font-medium">{c.name}</span>
                    {c.category?.name && (
                      <span className="shrink-0 text-xs text-muted-foreground">{c.category.name}</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-3 py-2.5 text-sm text-muted-foreground">未找到匹配的客户</p>
            )}
          </div>
        )}
      </div>
      <DateRangePicker
        value={{ start_date: value.start_date, end_date: value.end_date }}
        onChange={range => onChange({ ...value, start_date: range.start_date, end_date: range.end_date })}
      />

      <EmployeeTreeFilter
        label=""
        placeholder="录入人"
        hideDepartmentScope
        value={value.user_ids}
        onChange={ids => onChange({ ...value, user_ids: ids.length > 0 ? ids : undefined })}
      />

      <DepartmentScopeFilter
        value={{ scope: value.scope, department_id: value.department_id }}
        onChange={scopeValue => onChange({ ...value, department_id: undefined, ...scopeValue })}
      />

      <div className="flex gap-2">
        <Button type="button" onClick={onSearch} disabled={searching}>
          <Search className="mr-1.5 h-4 w-4" />
          查询
        </Button>
        <Button type="button" variant="outline" onClick={onReset}>
          <RotateCcw className="mr-1.5 h-4 w-4" />
          重置
        </Button>
      </div>
    </div>
  )
}
